import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { PortTransitTime } from '../interfaces/mock.interface';
import { Clock, TrendingUp, Package } from 'lucide-react';

interface PortTransitTimesWidgetProps {
  transitTimes: PortTransitTime[];
  maxItems?: number;
}

export const PortTransitTimesWidget = ({
  transitTimes,
  maxItems = 6,
}: PortTransitTimesWidgetProps) => {
  if (transitTimes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Timp Mediu de Tranzit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Nu există date disponibile pentru timp de tranzit.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Limit to maxItems
  const limitedTransitTimes = transitTimes.slice(0, maxItems);

  // Find max transit time for progress bar scaling
  const maxTransitTime = Math.max(...limitedTransitTimes.map(t => t.averageTransitTime));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Timp Mediu de Tranzit pe Porturi
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Cele mai frecvente {maxItems} rute
        </p>
      </CardHeader>
      <CardContent>
        {/* Horizontal Grid Layout - 4 cards per row */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {limitedTransitTimes.map((transit, index) => (
            <div
              key={index}
              className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200 space-y-3 shadow-sm hover:shadow-md"
            >
              {/* Route */}
              <div className="space-y-1">
                <div className="text-sm font-semibold text-foreground">
                  {transit.route}
                </div>
                <div className="text-xs text-muted-foreground">
                  {transit.portName}
                </div>
              </div>

              {/* Transit Time - Large Display */}
              <div className="flex items-baseline gap-1">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-3xl font-bold text-primary">
                  {transit.averageTransitTime}
                </span>
                <span className="text-sm text-muted-foreground">zile</span>
              </div>

              {/* Progress Bar */}
              <Progress
                value={(transit.averageTransitTime / maxTransitTime) * 100}
                className="h-2"
              />

              {/* Container Count */}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Package className="h-3 w-3" />
                <span>{transit.containerCount} containere</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
